import { FC } from 'react';
import { Box, Typography } from '@mui/material';

import { IPlan } from '../Plans/Plans';
import { formatPriceParts, getPlanPricing } from '../../utils/planPricing';

import styles from './PlanPrice.module.scss';

type PlanPriceProps = {
  plan: IPlan;
  currencySymbol: string;
  className?: string;
};

const CENTS_SUP_STYLE = { fontSize: '0.6em', marginLeft: '1px' } as const;
const PER_YEAR_SX = { marginLeft: '4px' } as const;

const PlanPrice: FC<PlanPriceProps> = ({ plan, currencySymbol, className }) => {
  const paymentClassName = `${styles.payment} ${className ?? ''}`.trim();

  if (plan.price_in_cents === 0) {
    return (
      <Typography
        variant="h6Bold"
        className={paymentClassName}
      >
        Free
      </Typography>
    );
  }

  const pricing = getPlanPricing(plan);
  const finalParts = formatPriceParts(pricing.finalCents);
  const originalParts = pricing.originalCents != null ? formatPriceParts(pricing.originalCents) : null;

  const renderCurrentPrice = (parts: { dollars: number; cents: string }) => (
    <Typography
      variant="h6Bold"
      className={paymentClassName}
      component="span"
      sx={{ display: 'inline' }}
    >
      {currencySymbol}
      {parts.dollars}
      <sup style={CENTS_SUP_STYLE}>{parts.cents}</sup>
      <Typography
        variant="h13"
        component="span"
        sx={PER_YEAR_SX}
      >
        /year
      </Typography>
    </Typography>
  );

  if (!pricing.hasReferralDiscount || !originalParts) {
    return renderCurrentPrice(finalParts);
  }

  return (
    <Box className={styles.discountedRow}>
      {renderCurrentPrice(finalParts)}
      <div className={styles.originalPriceWrapper}>
      <span className={styles.originalPrice}>
        {currencySymbol}
        {originalParts.dollars}
        <sup style={CENTS_SUP_STYLE}>{originalParts.cents}</sup>
        <span className={styles.originalPerYear}>/year</span>
      </span>
      <Typography
        variant="h13"
        component="span"
        className={styles.discountPercent}
      >
        ({pricing.discountPercent}% discount)
      </Typography>
      </div>
    </Box>
  );
};

export default PlanPrice;
